// Ask Ogma's side window (Craig, 30 Oct 2026; docs/specs/CAMPAIGN_LED_2026_10.md "11"): when an answer is a thing (a
// table, a chart, a draft campaign, a firm's story) it opens beside the chat, as Claude's artifacts do. It stays open
// while the person keeps talking, and asking ("only the ones in Scotland") replaces it. Everything in it can be exported.
// A draft campaign shows as a checklist of what it needs, each changeable here (CampaignChecklist), and ends with Review
// and confirm: it is saved as a draft, as the person, and opens on its own page to approve. Nothing is sent or approved here.
// A table made from a query (31 Oct 2026, replacing Ask the CRM at /ask/table) is live here: filters, sorting, columns,
// the whole-answer download, saving as a view and acting on the list (AskTable).
// A page of the app opens here too (Craig, 31 Oct 2026: "without leaving the chat page"): the real page in a frame on
// the same address and sign-in (lib/embed.ts), where the person acts with its own buttons, so a send, an approval or a
// quote happens beside the chat, by the person's press, as on the full page. A quote opens with its facts.
// Its left edge drags to make it wider or narrower, and Wider fills the screen bar the chat (31 Oct 2026; lib/sideWidth.ts).
import { useEffect, useState, type KeyboardEvent, type PointerEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, ExternalLink, Maximize2, Minimize2, X } from "lucide-react";
import type { ChatSide } from "../../lib/chat";
import { saveCampaign } from "../../lib/campaigns";
import { campaignErrorText } from "../../lib/campaignEditor";
import { downloadCsv } from "../../lib/csv";
import { humanise } from "../../lib/format";
import { useCrm, useWorkspace } from "../../lib/session";
import { downloadDoc, draftInput, quoteLines, sideFile } from "../../lib/sideWindow";
import type { JsonObject } from "../../lib/types";
import { MetricChart } from "../MetricChart";
import { campaignLink } from "../find/CampaignList";
import { askSpecOf } from "../../lib/ask";
import { AskTable } from "./AskTable";
import { CampaignChecklist } from "./CampaignChecklist";
import { applySide, clampSide, savedSide, STEP, widestSide } from "../../lib/sideWidth";

const shown = (v: unknown) => (v === null || v === undefined || v === "" ? "—" : typeof v === "boolean" ? (v ? "Yes" : "No") : typeof v === "object" ? JSON.stringify(v) : String(v));

function title(s: ChatSide): string {
  if (s.kind === "table") return s.title;
  if (s.kind === "chart") return s.chart.title;
  if (s.kind === "campaign") return `${s.draft.name} (draft)`;
  if (s.kind === "page") return s.title;
  if (s.kind === "quote") return String(s.quote.label ?? "Quote");
  return "The firm's story";
}

function ReviewAndConfirm({ side }: { side: Extract<ChatSide, { kind: "campaign" }> }) {
  const crm = useCrm();
  const { tenant } = useWorkspace();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ready = side.draft.reach === "known" || Boolean(side.draft.who);
  const go = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await saveCampaign(crm, draftInput(side.draft, (tenant?.config ?? null) as JsonObject | null));
      navigate(campaignLink(r.id));
    } catch (e) {
      setErr(campaignErrorText((e as Error).message));
      setBusy(false);
    }
  };
  return (
    <div className="chat-side-confirm">
      <button type="button" className="btn btn-primary" onClick={() => void go()} disabled={busy || !ready}>
        {busy ? "Saving the draft…" : "Review and confirm"}
      </button>
      <span className="muted small">
        {ready ? "Opens it as a draft on its own page, where you approve it. Nothing is sent before that." : "Tell Ogma who to target first."}
      </span>
      {err && <span className="small" role="alert">{err}</span>}
    </div>
  );
}

/** The side window's width: dragged by its edge, stepped with the arrow keys there, or Wider and back. */
function useSideWidth() {
  const [width, setWidth] = useState<number | null>(() => savedSide());
  useEffect(() => {
    applySide(width === null ? null : clampSide(width, window.innerWidth), false);
  }, [width]);
  const set = (w: number | null) => {
    const next = w === null ? null : clampSide(w, window.innerWidth);
    setWidth(next);
    applySide(next);
  };
  const current = () => width ?? document.querySelector(".chat-side")?.getBoundingClientRect().width ?? 576;
  const drag = (e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const right = (e.currentTarget.parentElement?.getBoundingClientRect().right) ?? window.innerWidth;
    const move = (m: globalThis.PointerEvent) => set(right - m.clientX);
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
      document.documentElement.classList.remove("is-resizing-side");
    };
    document.documentElement.classList.add("is-resizing-side");
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
  };
  const key = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    set(current() + (e.key === "ArrowLeft" ? STEP : -STEP));
  };
  const wide = width !== null && width >= widestSide(window.innerWidth);
  return { width, drag, key, wide, toggle: () => set(wide ? null : widestSide(window.innerWidth)) };
}

/** onChange: the person changed the draft campaign here; onAsk: asks Ogma in the chat ("Ask me" on a step). */
export function SideWindow({ side, onClose, onChange, onAsk }: { side: ChatSide; onClose: () => void; onChange?: (s: ChatSide) => void; onAsk?: (text: string) => void }) {
  // The page makes room for it on wide screens (assistant.css), so the conversation stays readable beside it.
  useEffect(() => {
    document.documentElement.classList.add("has-chat-side");
    return () => document.documentElement.classList.remove("has-chat-side");
  }, []);
  const size = useSideWidth();
  // A live table brings its own download (the whole answer), so the footer's copy of the first rows is not shown.
  const live = side.kind === "table" ? askSpecOf(side.spec) : null;
  const file = live ? null : sideFile(side);
  const quoteFirm = side.kind === "quote" && typeof side.quote.organisation_id === "string" ? side.quote.organisation_id : null;
  const full = side.kind === "page" ? side.path : quoteFirm ? `/contacts/companies/${quoteFirm}?tab=deals` : null;
  const exportIt = () => {
    const f = file;
    if (!f) return;
    if (f.type === "csv") downloadCsv(f.filename, f.content);
    else downloadDoc(f.filename, f.content);
  };
  const exportLabel = side.kind === "table" ? "Download as a spreadsheet" : side.kind === "chart" ? "Download the figures" : "Download as a document";
  return (
    <aside className="chat-side" aria-label={title(side)}>
      <div className="chat-side-grip" role="separator" aria-orientation="vertical" aria-label="Drag to resize the side window"
        aria-valuenow={size.width ?? undefined} tabIndex={0} onPointerDown={size.drag} onKeyDown={size.key} />
      <div className="chat-side-head">
        <h3>{title(side)}</h3>
        <button type="button" className="btn-icon chat-side-wider" onClick={size.toggle}
          aria-label={size.wide ? "Make the side window narrower" : "Make the side window wider"} title={size.wide ? "Narrower" : "Wider"}>
          {size.wide ? <Minimize2 size={16} aria-hidden="true" focusable="false" /> : <Maximize2 size={16} aria-hidden="true" focusable="false" />}
        </button>
        {full && (
          <Link to={full} className="btn btn-quiet btn-small" title="Leave the chat for the full page">
            <ExternalLink size={14} aria-hidden="true" focusable="false" /> Full page
          </Link>
        )}
        <button type="button" className="btn-icon" aria-label="Close the side window" onClick={onClose}>
          <X size={18} aria-hidden="true" focusable="false" />
        </button>
      </div>
      <div className="chat-side-body">
        {live && <AskTable key={JSON.stringify(live)} spec={live} title={title(side)} />}
        {side.kind === "table" && !live && (
          <>
            <p className="muted small">
              {side.total === 0 ? "None yet." : side.file.rows.length < side.total ? `The first ${side.file.rows.length} of ${side.total}.` : `${side.total} in all.`}
              {side.spec && side.total > 0 ? ' Ask to change it, for example "only the ones in Scotland".' : ""}
            </p>
            {side.total > 0 && <div className="table-wrap">
              <table>
                <thead>
                  <tr>{side.file.columns.slice(0, 6).map((c) => <th key={c} scope="col">{humanise(c)}</th>)}</tr>
                </thead>
                <tbody>
                  {side.file.rows.slice(0, 50).map((r, i) => (
                    <tr key={i}>{side.file.columns.slice(0, 6).map((c) => <td key={c}>{shown(r[c])}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>}
            {(side.file.columns.length > 6 || side.file.rows.length > 50) && <p className="muted small">The download has every row and column.</p>}
          </>
        )}
        {side.kind === "chart" && (
          <>
            <MetricChart result={side.chart.result} caption={side.chart.title} />
            <p className="small muted">{side.chart.summary}</p>
          </>
        )}
        {side.kind === "campaign" && (
          <>
            <CampaignChecklist draft={side.draft} onAsk={onAsk} onChange={onChange ? (draft) => onChange({ kind: "campaign", draft }) : undefined} />
            <ReviewAndConfirm side={side} />
          </>
        )}
        {side.kind === "page" && (
          // The real page, signed in as the person, without the menu (lib/embed.ts). key: a new page loads afresh.
          <iframe key={side.path} className="chat-side-frame" src={side.path} title={side.title} />
        )}
        {side.kind === "quote" && (
          <dl className="chat-side-draft">
            {quoteLines(side.quote).map((l) => (
              <div key={l.label}>
                <dt>{l.label}</dt>
                <dd>{l.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {side.kind === "firm" && (
          <>
            <p className="small"><Link to={`/contacts/companies/${side.id}`}>Open the firm's page</Link></p>
            {side.items.length === 0 ? <p className="muted small">Nothing recorded yet.</p> : (
              <ol className="chat-side-story">
                {side.items.slice(0, 30).map((it, i) => (
                  <li key={i}>
                    <span className="muted small">{shown(it.at ?? it.occurred_at).slice(0, 10)}</span>{" "}
                    {shown(it.summary ?? it.subject ?? it.title ?? it.kind)}
                  </li>
                ))}
              </ol>
            )}
          </>
        )}
      </div>
      {file && (
        <div className="chat-side-foot">
          <button type="button" className="btn btn-quiet" onClick={exportIt}>
            <Download size={16} aria-hidden="true" focusable="false" /> {exportLabel}
          </button>
        </div>
      )}
    </aside>
  );
}
